import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Eye } from "lucide-react";
import type { PolicyTemplate } from "@/lib/template-utils";

interface TemplateCardProps {
  template: PolicyTemplate;
  onPreview: (template: PolicyTemplate) => void;
  onUse?: (template: PolicyTemplate) => void;
  showUsageCount?: boolean;
}

export function TemplateCard({ template, onPreview, onUse, showUsageCount }: TemplateCardProps) {
  const toTitleCase = (value?: string | null) => {
    if (!value) return '';
    return value
      .replace(/[_-]+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Card className="shadow-card hover:shadow-elevated transition-shadow transition-colors hover:bg-muted/60 flex flex-col h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg leading-tight flex items-start gap-2">
          <FileText className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <span className="line-clamp-2">{template.title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-3 p-6 pt-0">
        {template.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {template.description}
          </p>
        )}
        
        <div className="flex flex-wrap gap-2">
          {template.category && (
            <Badge variant="outline" className="text-xs">
              {toTitleCase(template.category)}
            </Badge>
          )}
          {template.compliance_framework && (
            <Badge variant="secondary" className="text-xs">
              {template.compliance_framework}
            </Badge>
          )}
        </div>
      </CardContent>
      {showUsageCount && template.template_type === 'internal' && (
        <div className="px-6 text-xs text-muted-foreground mb-2">Used {template.usage_count} times</div>
      )}
      <div className="pt-4 border-t px-6 pb-4 mt-auto">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPreview(template)}
            className="flex-1"
          >
            <Eye className="w-3 h-3 mr-1" />
            Preview
          </Button>
          {onUse && (
            <Button
              size="sm"
              onClick={() => onUse(template)}
              className="flex-1"
            >
              Use Template
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

